import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HelpCircle,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  X,
  Save,
  Loader
} from 'lucide-react';
import faqsService from '../services/faqsService';
import { useToast } from '../context/ToastContext';
import './FAQsPage.css';

const FAQsPage = () => {
  useTranslation();
  const toast = useToast();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [formData, setFormData] = useState({
    questionAr: '',
    questionEn: '',
    answerAr: '',
    answerEn: '',
    category: 'general',
    order: 0,
    isPublished: true
  });
  const [saving, setSaving] = useState(false);

  const categories = [
    { value: 'general', label: 'عام' },
    { value: 'account', label: 'الحساب' },
    { value: 'offers', label: 'العروض' },
    { value: 'requests', label: 'الطلبات' },
    { value: 'payments', label: 'المدفوعات' },
    { value: 'subscription', label: 'الاشتراكات' },
    { value: 'technical', label: 'تقني' },
    { value: 'other', label: 'أخرى' }
  ];

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterActive !== 'all') params.isPublished = filterActive;
      
      const response = await faqsService.getAll(params);
      if (response.success) {
        setFaqs(response.data.faqs || []);
      }
    } catch (err) {
      console.error('Error fetching FAQs:', err);
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterActive]);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const filteredFaqs = faqs.filter(faq => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!faq.questionAr?.toLowerCase().includes(query) &&
          !faq.questionEn?.toLowerCase().includes(query) &&
          !faq.answerAr?.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });

  const handleOpenModal = (mode, faq = null) => {
    setModalMode(mode);
    setSelectedFaq(faq);
    if (faq) {
      setFormData({
        questionAr: faq.questionAr || '',
        questionEn: faq.questionEn || '',
        answerAr: faq.answerAr || '',
        answerEn: faq.answerEn || '',
        category: faq.category || 'general',
        order: faq.order || 0,
        isPublished: faq.isPublished !== false
      });
    } else {
      setFormData({
        questionAr: '',
        questionEn: '',
        answerAr: '',
        answerEn: '',
        category: 'general',
        order: faqs.length,
        isPublished: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFaq(null);
    setFormData({
      questionAr: '',
      questionEn: '',
      answerAr: '',
      answerEn: '',
      category: 'general',
      order: 0,
      isPublished: true
    });
  };

  const handleSave = async () => {
    if (!formData.questionAr || !formData.questionEn || !formData.answerAr || !formData.answerEn) {
      toast.warning('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    setSaving(true);
    try {
      if (modalMode === 'create') {
        const response = await faqsService.create(formData);
        if (response.success) {
          fetchFaqs();
          handleCloseModal();
        }
      } else {
        const response = await faqsService.update(selectedFaq._id, formData);
        if (response.success) {
          fetchFaqs();
          handleCloseModal();
        }
      }
    } catch (err) {
      toast.error(err.message || 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await faqsService.update(faq._id, { isPublished: !faq.isPublished });
      fetchFaqs();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`هل أنت متأكد من حذف هذا السؤال؟`)) return;
    
    try {
      await faqsService.delete(faq._id);
      fetchFaqs();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="faqs-page">
      <div className="page-header">
        <div className="header-title">
          <HelpCircle size={28} />
          <div>
            <h1>إدارة الأسئلة الشائعة</h1>
            <p>إضافة وتعديل الأسئلة الشائعة للمستخدمين</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => handleOpenModal('create')}>
          <Plus size={18} />
          إضافة سؤال
        </button>
      </div>

      {/* Stats */}
      <div className="faqs-stats">
        <div className="stat-card">
          <span className="stat-value">{faqs.length}</span>
          <span className="stat-label">إجمالي الأسئلة</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{faqs.filter(f => f.isPublished).length}</span>
          <span className="stat-label">أسئلة نشطة</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{new Set(faqs.map(f => f.category)).size}</span>
          <span className="stat-label">تصنيفات</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="بحث في الأسئلة..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="all">جميع التصنيفات</option>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
        >
          <option value="all">جميع الحالات</option>
          <option value="true">نشط</option>
          <option value="false">غير نشط</option>
        </select>
      </div>

      {/* FAQs List */}
      {loading ? (
        <div className="loading-state">
          <Loader className="spinner" size={32} />
          <p>جاري التحميل...</p>
        </div>
      ) : filteredFaqs.length === 0 ? (
        <div className="empty-state">
          <HelpCircle size={64} />
          <h3>لا توجد أسئلة</h3>
          <p>ابدأ بإضافة الأسئلة الشائعة للمستخدمين</p>
          <button className="btn btn-primary" onClick={() => handleOpenModal('create')}>
            <Plus size={18} />
            إضافة سؤال
          </button>
        </div>
      ) : (
        <div className="faqs-list">
          {filteredFaqs.map(faq => (
            <div key={faq._id} className={`faq-item ${!faq.isPublished ? 'inactive' : ''} ${expandedFaq === faq._id ? 'expanded' : ''}`}>
              <div 
                className="faq-header"
                onClick={() => setExpandedFaq(expandedFaq === faq._id ? null : faq._id)}
              >
                <div className="faq-question">
                  <span className="category-badge">
                    {categories.find(c => c.value === faq.category)?.label || faq.category}
                  </span>
                  <h3>{faq.questionAr}</h3>
                  {!faq.isPublished && <span className="inactive-badge">غير نشط</span>}
                </div>
                <div className="faq-toggle">
                  {expandedFaq === faq._id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>
              {expandedFaq === faq._id && (
                <div className="faq-content">
                  <p>{faq.answerAr}</p>
                  <div className="faq-actions">
                    <span className="order-badge">الترتيب: {faq.order}</span>
                    <div className="action-buttons">
                      <button 
                        className="action-btn"
                        onClick={() => handleToggleActive(faq)}
                        title={faq.isPublished ? 'إخفاء' : 'إظهار'}
                      >
                        {faq.isPublished ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => handleOpenModal('edit', faq)}
                        title="تعديل"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDelete(faq)}
                        title="حذف"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <HelpCircle size={24} />
                {modalMode === 'create' ? 'إضافة سؤال جديد' : 'تعديل السؤال'}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {/* Arabic Fields */}
              <div className="form-row">
                <div className="form-group">
                  <label>السؤال بالعربي *</label>
                  <input
                    type="text"
                    value={formData.questionAr}
                    onChange={(e) => setFormData({ ...formData, questionAr: e.target.value })}
                    placeholder="اكتب السؤال بالعربي..."
                  />
                </div>
                <div className="form-group">
                  <label>السؤال بالإنجليزي *</label>
                  <input
                    type="text"
                    value={formData.questionEn}
                    onChange={(e) => setFormData({ ...formData, questionEn: e.target.value })}
                    placeholder="Write question in English..."
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>الإجابة بالعربي *</label>
                  <textarea
                    value={formData.answerAr}
                    onChange={(e) => setFormData({ ...formData, answerAr: e.target.value })}
                    placeholder="اكتب الإجابة بالعربي..."
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label>الإجابة بالإنجليزي *</label>
                  <textarea
                    value={formData.answerEn}
                    onChange={(e) => setFormData({ ...formData, answerEn: e.target.value })}
                    placeholder="Write answer in English..."
                    rows={4}
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>التصنيف</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>الترتيب</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                    min={0}
                  />
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  />
                  نشط (ظاهر للمستخدمين)
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <Loader className="spinner" size={18} /> : <Save size={18} />}
                {modalMode === 'create' ? 'إضافة' : 'حفظ التعديلات'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQsPage;
